import { Module } from "vuex";
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { serializer } from '@/api/shared/serializer';
import { NotificationTypeEnum } from "@/enums/NotificationTypeEnum";
import moment from "moment";
import {StaffAccessLevels} from "@/enums/StaffAccessLevels";
import { UserTypeEnum } from "@/enums/UserTypeEnum";
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';
import { StaffUser } from '@/models/StaffUser';
import { ModelWithRef } from '@/models/base/ModelWithRef';
import { trimStringFields } from '@/utils/stringFormat';
import { FeatureFlags, isFeatureEnabled } from '@/consts/featureFlags';

const userModule: Module<any, any> = {
  namespaced: false,
  state: {
    users: [],
    maintenanceUsers: [],
    maintenanceVendors: [],
    selecteduser: null,
    facilitiesForOrg: [],
    incomingWebNotifications: [],
    usersRoles: [],
    userManagementStaffUsersListFilters: {search: '', selectedFacilitiesIds: []}
  },

  mutations: {
    setusers(state: any, payload: []) {
      state.users = payload;
    },
    setFacilitiesForOrg(state: any, payload: []) {
      state.facilitiesForOrg = payload;
    },

    setSelecteduser(state: any, payload: []) {
      state.selecteduser = payload;
    },

    setWebNotifications(state: any, payload: []) {
      state.incomingWebNotifications = payload;
    },

    setUsersRoles(state: any, payload: []) {
      state.usersRoles = payload
    },

    setMaintenanceUsers(state: any, payload: []) {
      state.maintenanceUsers = payload;
    },

    setMaintenanceVendors(state: any, payload: []) {
      state.maintenanceVendors = payload;
    },

    setUserManagementStaffUsersListFilters(state: any, payload: any){
      state.userManagementStaffUsersListFilters = payload;
    },
  },

  actions: {
    async getMaintenanceUsers({ commit }) {
      const facilityId = this.state.authModule.currentFacility;
      const users = firebase
        .firestore()
        .collection("users")
        .where("facilitiesIds", "array-contains", facilityId)
        .where("orgId", "==", this.state.authModule.currentFacilityData.orgId)
        .where("accessLevel", "in", [StaffAccessLevels.MaintenanceAdmin, StaffAccessLevels.MaintenanceUser]);

      await users.get().then((querySnapshot) => {
        const usersList = serializer.querySnapshotToDocuments(querySnapshot);
        commit("setMaintenanceUsers", usersList);
      });
    },

    async getMaintenanceVendors({ commit }) {
      const facilityId = this.state.authModule.currentFacility;
      const users = firebase
        .firestore()
        .collection("vendors")
        .where("facilityId", "==", facilityId)
        .where("type", "==", "Maintenance")

      await users.get().then((querySnapshot) => {
        const usersList = serializer.querySnapshotToDocuments(querySnapshot);
        commit("setMaintenanceVendors", usersList);
      });
    },

    async getFacilitiesForOrg({ commit }, orgId = null ) {
      const facility = this.state.authModule.currentFacilityData;
      const facilities = firebase.firestore().collection("facility").where("orgId", "==", orgId ?? facility.orgId);
      await facilities.get().then((querySnapshot) => {
        const facilitiesList: Array<any> = [];

        querySnapshot.docs.forEach((x) => {
          const thisRes = x.data();
          thisRes.id = x.id;
          facilitiesList.push(thisRes);
        });
        commit("setFacilitiesForOrg", facilitiesList);
      });
    },

    async getIncomingWebNotifications({ commit }) {
      if (!this.state.authModule.user.options || !this.state.authModule.user.options.webNotifications) return

      const facilityId = this.state.authModule.currentFacility
      const facility = this.state.authModule.currentFacilityData;
      const staffFamilyMessagingEnabled = isFeatureEnabled(facility, FeatureFlags.staffFamilyMessagingEnabled);
      const userWebNotificationsOptions = this.state.authModule.user.options.webNotifications
      const user = this.state.authModule.user;
      const userId = this.state.authModule.user.id;
      const fromDate: any = user?.createdDateTime ? moment(user?.createdDateTime.seconds * 1000).toDate() : null;
      const isUserAdmin: boolean = [StaffAccessLevels.OrgAdmin, StaffAccessLevels.HomeAdmin].includes(user.accessLevel);
      const isMaintenanceUser: boolean = [StaffAccessLevels.MaintenanceAdmin, StaffAccessLevels.MaintenanceUser].includes(user.accessLevel);
      const isHomeUser: boolean = user.accessLevel === StaffAccessLevels.HomeUser;

      let query;
      if(fromDate) {
        query = firebase.firestore().collection("notifications")
          .where("facilityId", "==", facilityId)
          .where("to", "==", "staff")
          .where("dateTimeCreated", ">", fromDate)
          .orderBy("dateTimeCreated", "desc")
      } else {
        query = firebase.firestore().collection("notifications")
          .where("facilityId", "==", facilityId)
          .where("to", "==", "staff")
          .orderBy("dateTimeCreated", "desc")
      }

      const queryResult = await query.get()

      let notifications = serializer.querySnapshotToDocuments(queryResult) as any[];

      if (!userWebNotificationsOptions.serviceRequestCreated) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.ServiceRequestCreated)
      } if (!userWebNotificationsOptions.serviceRequestCompleted) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.ServiceRequestCompleted)
      } if (!userWebNotificationsOptions.unattendedEventEnded) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.UnattendedEventEnded)
      } if (!userWebNotificationsOptions.maintenanceRequestCreated) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.MaintenanceRequestCreated)
      } if (!userWebNotificationsOptions.maintenanceRequestComment) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.MaintenanceRequestComment)
      } if (!userWebNotificationsOptions.deviceAssigned) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.DeviceAssigned)
      } if (!userWebNotificationsOptions.deviceUnassigned) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.DeviceUnassigned)
      } if (!userWebNotificationsOptions.visitToResidentCreated) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.VisitCreated)
      } if (!userWebNotificationsOptions.bookingsAndOrdersComment) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.BookingsAndOrdersComment)
      } if (!userWebNotificationsOptions.messagesFromFamily) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.Message && x.from != "family")
      } if (!userWebNotificationsOptions.updatesADT) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.TransferResident)
      } if (!userWebNotificationsOptions.maintenanceRequestWarning) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.MaintenanceRequestCompletionDeadline)
      } if (isMaintenanceUser) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.StaffAppResidentSuggestedUserStory)
      } if (isMaintenanceUser || isHomeUser) {
        // Maintenance users and Home Users should not receive any family feedback notifications
        notifications = notifications.filter(x => 
          x.type !== NotificationTypeEnum.FeedbackRequestCreated &&
          x.type !== NotificationTypeEnum.FeedbackRequestNewMessage &&
          x.type !== NotificationTypeEnum.FeedbackRequestStale &&
          x.type !== NotificationTypeEnum.FeedbackRequestClosed &&
          x.type !== NotificationTypeEnum.FeedbackRequestPending
        )
      } if (!userWebNotificationsOptions.familyFeedbackFamilyReplied) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.FeedbackRequestNewMessage)
      } if (!userWebNotificationsOptions.familyFeedbackRequiresUpdate) {
        notifications = notifications.filter(x=>x.type !== NotificationTypeEnum.FeedbackRequestStale)
      }

      notifications = notifications.filter(x => {
        const isRead = x.readByStaffs?.some(s => s.id === userId);
        const isStaffAppNotification = x.staffAppNotification;

        if (isRead || isStaffAppNotification) return false;

        if (x.type === NotificationTypeEnum.MaintenanceRequestCompletionDeadline && !x.staffUserIds?.includes(userId) && !isRead) return false;

        if (userWebNotificationsOptions.messagesFromFamily && x.type === NotificationTypeEnum.Message && !isRead && (isUserAdmin ? !isUserAdmin : !x.staffUserIds?.includes(userId))) return false;

        if (x.type === NotificationTypeEnum.HomeMessage && (!staffFamilyMessagingEnabled || !x.staffUserIds?.includes(userId))) return false;

        return true;
      });

      console.log(`loaded (${notifications.length}) notifications`)

      commit("setWebNotifications", notifications);
    },

    async updateUser({ dispatch, commit }, user) {
      if(!user) throw new Error('User not passed')

      user.updatedById = user?.updatedById ?? this.state.authModule.user.id;
      user.updatedByType = user?.updatedByType ?? UserTypeEnum.Staff;
      user.typesOfRecordChanged = user?.typesOfRecordChanged ?? [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.UserManagement}`],
      user.initiatorAccessLevel = this.state.authModule.user.accessLevel
      user.initiatorName = this.state.authModule.user.name

      trimStringFields(user);

      await firebase
        .firestore()
        .collection("users")
        .doc(user.id)
        .update(user)
        .then(async() => {
          const updatedUser = await dispatch("getSelectedUser", user.id)
          if(this.state.authModule.user.id === user.id) {
            dispatch("setUserData", updatedUser);
          }
        });
    },

    async deleteUser({ dispatch, commit }, id) {
      await firebase
        .firestore()
        .collection("users")
        .doc(id)
        .update({
          deletedById: this.state.authModule.user.id,
          deletedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.UserManagement}`],
          initiatorAccessLevel: this.state.authModule.user.accessLevel,
          initiatorName: this.state.authModule.user.name,
        }).then(async()=>{
           return await firebase
            .firestore()
            .collection("users")
            .doc(id)
            .delete().then(() => {
              dispatch("getCalenderEvents");
              return true;
            })
        })
     
    },

    async getSelectedUser({ commit }, id) {
      const user = firebase.firestore().collection("users").doc(id);

        return await user.get().then((querySnapshot) => {
          const currentUser = querySnapshot.data()
          if (currentUser != null) {
            currentUser.id = querySnapshot.id
            commit("setSelecteduser", currentUser)
            return currentUser
          }
      })
    },

    async updateFacilityName(_, data) {
      return await firebase
        .firestore()
        .collection("users")
        .where("orgId", "==", data.facilityOrgId)
        .where("facilitiesIds", 'array-contains', data.facilityId)
        .get()
        .then(snapshot => {
          snapshot.forEach(async doc => {
            const user = doc.data();
            const userId = doc.id;
            if (user.facilitiesIds?.find(x => x === data.facilityId) && user.facilities) {

              const facility = user.facilities.find(x => x.id == data.facilityId);
              facility.name = data.name;

              const facilitiesAll = user.facilities.filter(x => x.id != data.facilityId) || [];
              facilitiesAll.push(facility);

              if (facilitiesAll.length) {
                return await firebase
                  .firestore()
                  .collection("users")
                  .doc(userId)
                  .update({
                    facilities: facilitiesAll
                  }).catch(err => {
                    console.error(err)
                  })
              }
            }
          })
        })
    },

    async getUsersRoles({commit}) {
      return await firebase
        .firestore()
        .collection("usersRoles")
        .where("orgId", "==", this.state.authModule.currentFacilityData.orgId)
        .get().then((querySnapshot) => {
          const result: any[] = [];

          querySnapshot.docs.forEach(doc => {
            const role = doc.data();
            role.id = doc.id;
            result.push(role)
          });

          commit("setUsersRoles", result);
        })
    },

    async addUserRole({dispatch}, role) {
      role.createdById = this.state.authModule.user.id,
      role.createdByType = UserTypeEnum.Staff
      role.typesOfRecordChanged = [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.UserManagement}`],
      role.initiatorAccessLevel = this.state.authModule.user.accessLevel
      role.initiatorName = this.state.authModule.user.name

      return await firebase
        .firestore()
        .collection("usersRoles")
        .add(role)
        .then(async () => {
          dispatch("getUsersRoles")
        });
    },

    async deleteUserRole({dispatch}, roleId) {
      const firestore = firebase.firestore();

      await firestore
        .collection("users")
        .where("orgId", "==", this.state.authModule.currentFacilityData.orgId)
        .where("rolesIds", "array-contains", roleId)
        .get()
        .then(async (querySnapshot) => {
          const users: ModelWithRef<StaffUser>[] = await querySnapshot.docs.map(x => ({ id: x.id, ref: x.ref, ...x.data() }));

          for(const user of users) {
            const role = (await firestore.collection("usersRoles").doc(roleId).get()).data();

            if(role) {
              await user.ref.update({
                roles: firebase.firestore.FieldValue.arrayRemove(role.name),
                rolesIds: firebase.firestore.FieldValue.arrayRemove(roleId),
              })
            }
          }
        })

      await firestore
        .collection("usersRoles")
        .doc(roleId)
        .update({
          deletedById: this.state.authModule.user.id,
          deletedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.UserManagement}`],
          initiatorAccessLevel: this.state.authModule.user.accessLevel,
          initiatorName: this.state.authModule.user.name
        });

      return await firestore
        .collection("usersRoles")
        .doc(roleId)
        .delete()
        .then(() => {
          dispatch("getUsersRoles");
        })
    },

    async updateUserRole({dispatch}, role) {
      const firestore = firebase.firestore();

      await firestore
        .collection("usersRoles")
        .doc(role.id)
        .update({
          name: role.name,
          updatedById: this.state.authModule.user.id,
          updatedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.UserManagement}`],
          initiatorAccessLevel: this.state.authModule.user.accessLevel,
          initiatorName: this.state.authModule.user.name
        });

      return firestore
        .collection("users")
        .where("orgId", "==", this.state.authModule.currentFacilityData.orgId)
        .where("rolesIds", "array-contains", role.id)
        .get()
        .then(async (querySnapshot) => {
          const users: ModelWithRef<StaffUser>[] = await querySnapshot.docs.map(x => ({ id: x.id, ref: x.ref, ...x.data() }));

          for(const user of users) {
            const rolesIds = user?.rolesIds || [];
            const rolesDocs = await Promise.all(rolesIds.map(id => firestore.collection("usersRoles").doc(id).get()));
            const updatedRoles = rolesDocs.map(doc => doc?.data()?.name);
            await user.ref.update({ roles: updatedRoles })
          }

          dispatch("getUsersRoles");
        })
    },

    async storeStaffUsersListFilters({ commit }, filters) {
      if(!filters) return 

      commit("setUserManagementStaffUsersListFilters", filters);
    },

  },
};
export default userModule;
